import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useApi } from '../utils/useApi';

// Types
export interface UserRole {
  type: 'gym' | 'branch';
  gymId: string;
  gymName: string;
  branchId?: string;
  branchName?: string;
  role: string;
  permissions: string[];
  createdAt: string;
  hrmsPermissions?: string[];
  plan?: {
    name: string;
    features: Record<string, boolean>;
  } | null;
}

export interface ResourcePerm {
  view: boolean;
  edit: boolean;
}

export type ResourceKey =
  | 'reports'
  | 'expenses'
  | 'purchases'
  | 'ai'
  | 'dashboard'
  | 'notifications'
  | 'attendance';

export interface UserContextData {
  user: {
    id: string;
    name: string | null;
    displayName: string | null;
    phoneNo: string | null;
    email: string | null;
    avatar: string | null;
    status: string | null;
    lastLoginAt: string | null;
    defaultBranchId: string | null;
    onboardingComplete: boolean;
    currentOnboardingStep: number;
  };
  roles: UserRole[];
  accessibleGyms: string[];
  accessibleBranches: string[];
  // Per-gym resource permissions: { [gymId]: { [resource]: { view, edit } } }
  resourcePermissions: Record<string, Record<string, ResourcePerm>>;
  permissions: string[];
}

interface UserContextType {
  userContext: UserContextData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasResourcePermission: (resource: ResourceKey, mode?: 'view' | 'edit') => boolean;
  isOwner: boolean;
  getRolesForGym: (gymId: string) => UserRole[];
  getRolesForBranch: (branchId: string) => UserRole[];
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserContextProviderProps {
  children: ReactNode;
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [userContext, setUserContext] = useState<UserContextData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { fetchWithAuth } = useApi();
  const location = useLocation();
  const isSaaS = location.pathname.startsWith('/saas');

  const fetchUserContext = useCallback(async () => {
    if (!user || isSaaS) {
      if (!user) setUserContext(null);
      return;
    }

    try {
      // Only show blocking loading state if we don't have context yet
      if (!userContext) {
        setLoading(true);
      }
      setError(null);

      const response = await fetchWithAuth('/api/auth/context', {
        method: 'GET',
      });

      if (response.success) {
        // Ensure we have valid data even if some fields are missing
        const contextData = {
          ...response.data,
          roles: response.data.roles || [],
          accessibleGyms: response.data.accessibleGyms || [],
          accessibleBranches: response.data.accessibleBranches || [],
          resourcePermissions: response.data.resourcePermissions || {},
          permissions: response.data.permissions || []
        };
        setUserContext(contextData);
      } else {
        setError(response.error || 'Failed to fetch user context');
      }
    } catch (err) {
      // Only log actual errors, not expected authentication issues
      if (err instanceof Error && !err.message.includes('authentication') && !err.message.includes('loading')) {
        console.error('Error fetching user context:', err);
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch user context');
    } finally {
      setLoading(false);
    }
  }, [user, fetchWithAuth, isSaaS]);

  // Fetch user context when user changes
  useEffect(() => {
    fetchUserContext();
  }, [fetchUserContext]); // Use the memoized function

  // Check if user has specific permission
  const hasPermission = (permission: string): boolean => {
    if (!userContext) return false;
    return userContext.permissions.includes(permission);
  };

  // Check if user has specific role
  const hasRole = (role: string): boolean => {
    if (!userContext) return false;
    return userContext.roles.some(r => r.role === role);
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    if (!userContext) return false;
    return userContext.roles.some(r => roles.includes(r.role));
  };

  // Owner of any accessible gym — owners bypass resource/branch restrictions.
  const isOwner = !!userContext?.roles.some(r => r.role === 'owner');

  // Resource-level view/edit permission for the active gym. Owners always pass.
  const hasResourcePermission = (resource: ResourceKey, mode: 'view' | 'edit' = 'view'): boolean => {
    if (!userContext) return false;
    if (isOwner) return true;
    const gymId = userContext.accessibleGyms[0];
    if (!gymId) return false;
    const perm = userContext.resourcePermissions?.[gymId]?.[resource];
    if (!perm) return false;
    return mode === 'edit' ? perm.edit : perm.view;
  };

  // Get roles for specific gym
  const getRolesForGym = (gymId: string): UserRole[] => {
    if (!userContext) return [];
    return userContext.roles.filter(r => r.gymId === gymId);
  };

  // Get roles for specific branch
  const getRolesForBranch = (branchId: string): UserRole[] => {
    if (!userContext) return [];
    return userContext.roles.filter(r => r.branchId === branchId);
  };

  const value: UserContextType = useMemo(() => ({
    userContext,
    loading,
    error,
    refetch: fetchUserContext,
    hasPermission,
    hasRole,
    hasAnyRole,
    hasResourcePermission,
    isOwner,
    getRolesForGym,
    getRolesForBranch,
  }), [userContext, loading, error, fetchUserContext, hasPermission, hasRole, hasAnyRole, hasResourcePermission, isOwner, getRolesForGym, getRolesForBranch]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Hook to use user context
export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};

// Hook for permission-based rendering
export const usePermission = (permission: string): boolean => {
  const { hasPermission } = useUserContext();
  return hasPermission(permission);
};

// Hook for role-based rendering
export const useRole = (role: string): boolean => {
  const { hasRole } = useUserContext();
  return hasRole(role);
};

// Hook for multiple role-based rendering
export const useAnyRole = (roles: string[]): boolean => {
  const { hasAnyRole } = useUserContext();
  return hasAnyRole(roles);
};

// Hook for resource-permission-based rendering (view/edit per resource)
export const useResourcePermission = (
  resource: ResourceKey,
  mode: 'view' | 'edit' = 'view'
): boolean => {
  const { hasResourcePermission } = useUserContext();
  return hasResourcePermission(resource, mode);
}; 